import _pick from 'lodash/pick';
import PropTypes from 'prop-types';
import React, { useState, useEffect, useCallback } from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router';
import { Hub } from 'aws-amplify/utils';
import { getCurrentUser, signOut, fetchUserAttributes, fetchAuthSession } from 'aws-amplify/auth';
import { AppName } from 'sl-api-connector';
import UploadProfile from 'src/components/UploadProfile/UploadProfile';
import i18n from 'src/i18n_en';
import { authOperations } from 'src/store/modules/auth';
import { logDev } from 'src/utils/app';
import { trackLogIn } from 'src/utils/mixpanel';
import { getLoginType, getSSOReferrer, handleSSORedirect, saveSSOReferrer } from 'src/utils/sso';
import { logInFireAndForget } from '../../store/modules/auth/operations';
import Loading from '../common/Loading';
import BeaconSignInContainer from './BeaconSignIn';
import SignIn from './SignIn';
import './SignIn.scss';
import { getRecaptchaToken } from 'src/utils/recaptcha';
import axios from 'axios';
import { amplifyFederatedSignIn, amplifySignOut, initializeAmplify } from 'src/utils/auth';
import { trackLoginBotCheck } from 'src/services/userTracking/utils/trackBotCheck';
import colors from 'src/utils/colors';
import { parseQueryString } from 'src/utils/browser';
import { isSsoHost } from 'src/utils/ssoReferrer';

export const LoginContainerConfigs = {
  [AppName.Advertising]: { bgColor: '#ECF9F6', textColor: '#16949B' },
  [AppName.Atlas]: { bgColor: colors.lightestBlue, textColor: colors.darkBlue },
  [AppName.Omni]: { bgColor: '#FDF2EA', textColor: '#A21671' },
  [AppName.Beacon]: { bgColor: colors.lightestBlue, textColor: colors.darkBlue },
  [AppName.Discover]: { bgColor: colors.lightestBlue, textColor: '#46A8F6' },
  Advisor: { bgColor: colors.lightestBlue, textColor: colors.darkBlue },
  AdManager: { bgColor: colors.lightestBlue, textColor: colors.darkBlue },
  ShopperOS: { bgColor: colors.lightestBlue, textColor: colors.darkBlue },
  ShopperOSV2: { bgColor: colors.lightestBlue, textColor: colors.darkBlue },
  ShopperAnalytics: { bgColor: colors.lightestBlue, textColor: colors.darkBlue },
  DigitalShelf: { bgColor: colors.lightestBlue, textColor: colors.darkBlue },
  Sync: { bgColor: colors.lightestBlue, textColor: colors.darkBlue }
};

const SignInContainer = (props) => {
  const { app, auth, clearErrorMessage, location, history, setErrorMessage, userLogIn, userLogInExternal } = props;

  // State hooks to replace the class component state
  const [buttonText, setButtonText] = useState(i18n.button_login);
  const [loading, setLoading] = useState(false);
  const [handlingSSOSignin, setHandlingSSOSignin] = useState(true);
  const [isProfileImageUploaded, setIsProfileImageUploaded] = useState(true);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [authProvider, setAuthProvider] = useState(null);
  const [customOAuthState, setCustomOAuthState] = useState(null);

  // Restore referrer from localStorage to URL if missing (happens after Cognito OAuth sign_out redirect)
  useEffect(() => {
    const currentQueryParams = parseQueryString();
    const urlReferrer = currentQueryParams.referrer;
    const storedReferrer = localStorage.getItem('x-sl-sso-signin-referrer');

    // If URL doesn't have referrer but localStorage does, restore it to URL
    if (!urlReferrer && storedReferrer && getLoginType() === 'sso') {
      const currentUrl = new URL(window.location.href);
      currentUrl.searchParams.set('referrer', storedReferrer);
      // Use replaceState to update URL without page reload or adding to history
      window.history.replaceState({}, '', currentUrl.toString());
    }
  }, []);

  // Set up a listener for auth events
  useEffect(() => {
    initializeAmplify();
    const hubListener = Hub.listen('auth', async ({ payload }) => {
      const { event } = payload;
      switch (event) {
        case 'signedIn':
          // Handle sign in completion
          try {
            setHandlingSSOSignin(true);
            const currentUser = await getCurrentUser();
            const userAttributes = await fetchUserAttributes();
            const username = localStorage.getItem('x-sl-signin-user-email');
            if (currentUser && username) {
              setEmail(username);
              const emailDomainSuffix = `@${username.toLowerCase().split('@')[1]}`;
              const usernameCheckFailed = !currentUser.username
                .toLowerCase()
                .endsWith(`${username.toLowerCase().split('@')[0].split('-')[0]}${emailDomainSuffix}`);
              const emailCheckFailed = userAttributes?.email?.toLowerCase() !== username.toLowerCase();

              if (usernameCheckFailed && emailCheckFailed) {
                setErrorMessage(i18n.errorMessage_basic);
                await signOut();
              } else {
                const { token, context } = await getRecaptchaToken('LOGIN');
                const session = await fetchAuthSession();
                const response = await userLogInExternal(
                  app.targetUrl,
                  username,
                  session.tokens.accessToken.toString(),
                  session.tokens.idToken.toString(),
                  session.tokens.accessToken.payload.exp,
                  getSSOReferrer().referrer,
                  token,
                  context.userAgent
                );
                await handleLoginResponse(
                  response.status,
                  response.redirect,
                  response.redirectType,
                  response.isProfileImageUploaded,
                  response.data
                );
              }
            }
          } catch (e) {
            console.log('error encountered', e);
            await signOut();
          } finally {
            setHandlingSSOSignin(false);
          }
          break;
        case 'customOAuthState':
          // The custom state is returned in the data parameter
          setCustomOAuthState(JSON.parse(payload.data));
          // Now you can redirect to the original URL
          break;
      }
    });

    return () => {
      hubListener();
    };
  }, []);

  // this will handle sign callback from aws cognito
  useEffect(() => {
    (async () => {
      setHandlingSSOSignin(true);
      setLoading(true);
      try {
        const currentUser = await getCurrentUser();
        const userAttributes = await fetchUserAttributes();
        const username = localStorage.getItem('x-sl-signin-user-email');
        if (currentUser && username) {
          setEmail(username);
          const emailDomainSuffix = `@${username.toLowerCase().split('@')[1]}`;
          const usernameCheckFailed = !currentUser.username
            .toLowerCase()
            .endsWith(`${username.toLowerCase().split('@')[0].split('-')[0]}${emailDomainSuffix}`);
          const emailCheckFailed = userAttributes?.email?.toLowerCase() !== username.toLowerCase();

          if (usernameCheckFailed && emailCheckFailed) {
            setErrorMessage(i18n.errorMessage_basic);
            await signOut();
          } else {
            const { token, context } = await getRecaptchaToken('LOGIN');
            const session = await fetchAuthSession();
            const response = await userLogInExternal(
              app.targetUrl,
              username,
              session.tokens.accessToken.toString(),
              session.tokens.idToken.toString(),
              session.tokens.accessToken.payload.exp,
              getSSOReferrer().referrer,
              token,
              context.userAgent
            );
            await handleLoginResponse(
              response.status,
              response.redirect,
              response.redirectType,
              response.isProfileImageUploaded,
              response.data
            );
          }
        }
      } catch (e) {
        console.log('error encountered', e);
        await signOut();
      }
      setLoading(false);
      setHandlingSSOSignin(false);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // useEffect to handle componentWillUnmount logic
  useEffect(() => {
    return () => {
      clearErrorMessage();
    };
  }, [clearErrorMessage]);

  // Handle email change
  const handleEmailChange = useCallback((newEmail) => {
    setEmail(newEmail?.trim());
  }, []);

  // Handle sign in change
  const handleSignInChange = useCallback(
    (value) => {
      clearErrorMessage();
      // Update all state values from the value object
      if (value.password) setPassword(value.password);
      if (value.buttonText !== undefined) setButtonText(value.buttonText);
      if (value.loading !== undefined) setLoading(value.loading);
      if (value.email !== undefined) setEmail(value.email);
      if (value.authProvider !== undefined) setAuthProvider(value.authProvider);
    },
    [clearErrorMessage]
  );

  // Get API base URL
  const getApiBaseUrl = useCallback((hostname) => {
    if (hostname.indexOf('stackline.com') >= 0) {
      return hostname.replace(/atlas|beacon-pro|beacon|ad|omni|discover/, 'api');
    } else {
      return 'api.stackline.com';
    }
  }, []);

  // Login to additional subdomains if applicable
  const loginIntoAdditionalSubDomainsIfApplicable = useCallback(
    async (originalBaseUrl, email, password) => {
      // eslint-disable-next-line no-constant-condition
      if (false && app.name === AppName.Advertising && originalBaseUrl.startsWith('ad')) {
        const apiBaseUrl = getApiBaseUrl(originalBaseUrl);
        if (originalBaseUrl !== apiBaseUrl) {
          logDev(app.stage, `Triggering an additional login request to ${apiBaseUrl}`);
          await logInFireAndForget(apiBaseUrl, email, password);
        }
      }
    },
    [app, getApiBaseUrl]
  );

  const handleLoginResponse = useCallback(
    async (status, redirect, redirectType, isProfileImageUploaded, data) => {
      const loginType = getLoginType();
      const { targetUrl } = app;

      // Track bot check result if available
      if (data && data.botCheckResult) {
        const { botCheckResult } = data;
        trackLoginBotCheck(botCheckResult, status === 'good');
      }

      let pathname;
      let search = '';
      if (status === 'bad') {
        setLoading(false);
        signOut();
        if (redirectType === 'local' && redirect) {
          history.push(redirect);
        } else {
          setButtonText(i18n.button_login);
        }
      } else if (status === 'good') {
        if (loginType === 'sso') {
          handleSSORedirect(data.ssoJwtToken);
        } else {
          await loginIntoAdditionalSubDomainsIfApplicable(targetUrl, email, password);

          setLoading(false);
          if (redirectType === 'window') {
            window.location.href = redirect;
            return;
          } else if (redirectType === 'local') {
            pathname = redirect;
            if (redirect !== '/subscribe' && location.state && location.state.from) {
              ({ pathname, search } = location.state.from);
            }
          }

          if (!isProfileImageUploaded && !data?.userConfig?.skippedProfileImageUpload) {
            setIsProfileImageUploaded(false);
            return;
          } else {
            setIsProfileImageUploaded(true);
          }

          trackLogIn();
          history.push({ pathname, search });
        }
      }
    },
    [
      history,
      location,
      loginIntoAdditionalSubDomainsIfApplicable,
      userLogInExternal,
      authProvider,
      app,
      email,
      password
    ]
  );

  // Form submit handler
  const handleFormSubmit = useCallback(
    async (event) => {
      event.preventDefault();
      setLoading(true);
      try {
        // Check if user is already authenticated (profile upload flow)
        try {
          const currentUser = await getCurrentUser();
          if (currentUser && !isProfileImageUploaded) {
            // User is already authenticated and just uploaded their profile image
            setIsProfileImageUploaded(true);
            setLoading(false);

            // Redirect to the appropriate page
            let pathname = '/';
            let search = '';
            if (location.state && location.state.from) {
              ({ pathname, search } = location.state.from);
            }

            trackLogIn();
            history.push({ pathname, search });
            return;
          }
        } catch (e) {
          // User is not authenticated, continue with normal login flow
        }

        if (!email) {
          setErrorMessage(i18n.errorMessage_login_noEmailPassword);
          setLoading(false);
          return;
        }

        const response = await axios.post(
          `https://${app.targetUrl}/api/user/LoginAuthProvider`,
          {
            username: email
          },
          {
            headers: {}
          }
        );
        const currentAuthProvider = response.data.loginAuthProvider;
        const loginType = getLoginType();

        if (currentAuthProvider !== 'cognito') {
          localStorage.setItem('x-sl-signin-user-email', email);
          saveSSOReferrer();
          setAuthProvider(currentAuthProvider);
          const ssoState = {
            loginType,
            ssoReferrer: getSSOReferrer().referrer,
            ssoRedirectUrl: getSSOReferrer().redirectUrl,
            identity_provider: currentAuthProvider
          };
          await amplifyFederatedSignIn(currentAuthProvider, JSON.stringify(ssoState));
          return;
        } else if (currentAuthProvider !== authProvider) {
          setAuthProvider(currentAuthProvider);
          setLoading(false);
          return;
        }

        if (!email || !password) {
          setErrorMessage(i18n.errorMessage_login_noEmailPassword);
          setLoading(false);
          return;
        }

        setButtonText(i18n.button_loggingIn);

        const { targetUrl } = app;

        // Get reCAPTCHA token using the utility function
        const { token, context } = await getRecaptchaToken('LOGIN');

        const {
          status,
          redirect,
          redirectType,
          isProfileImageUploaded: userIsProfileImageUploaded,
          data
        } = await userLogIn(targetUrl, email, password, getSSOReferrer().referrer, token, context.userAgent);

        await handleLoginResponse(status, redirect, redirectType, userIsProfileImageUploaded, data);
      } finally {
        setLoading(false);
      }
    },
    [
      authProvider,
      email,
      password,
      app,
      setErrorMessage,
      userLogIn,
      location,
      history,
      loginIntoAdditionalSubDomainsIfApplicable,
      handleLoginResponse,
      isProfileImageUploaded
    ]
  );

  const { message } = auth;

  // Show loading spinner if we are on sso domain and get config call is not yet completed
  if (isSsoHost(window.location.host) && !app?.config?.domainConfig?.domainName) {
    return <Loading style={{ position: 'fixed' }} className="spinner" />;
  }

  const LoginContainer = BeaconSignInContainer;

  if (handlingSSOSignin) {
    return (
      <div
        style={{
          display: 'flex',
          width: '100%',
          height: '100%'
        }}
      >
        <div style={{ flex: 1, minWidth: '50%', zIndex: 0 }}>
          <LoginContainer appName={app.name} notificationMSG={message}>
            <Loading className="spinner" />
          </LoginContainer>
        </div>
      </div>
    );
  }

  return (
    <div
      style={{
        display: 'flex',
        width: '100%',
        height: '100%'
      }}
    >
      <div style={{ flex: 1, minWidth: '50%', zIndex: 0 }}>
        <LoginContainer appName={app.name} notificationMSG={message}>
          {!isProfileImageUploaded ? (
            <UploadProfile
              email={email}
              clearErrorMessage={clearErrorMessage}
              setErrorMessage={setErrorMessage}
              handleFormSubmit={handleFormSubmit}
            />
          ) : (
            <SignIn
              app={app}
              buttonText={buttonText}
              handleChange={handleSignInChange}
              handleFormSubmit={handleFormSubmit}
              loading={loading}
              email={email}
              authProvider={authProvider}
              handleEmailChange={handleEmailChange}
            />
          )}
        </LoginContainer>
      </div>
    </div>
  );
};

SignInContainer.propTypes = {
  app: PropTypes.object.isRequired,
  auth: PropTypes.object.isRequired,
  clearErrorMessage: PropTypes.func.isRequired,
  location: PropTypes.object.isRequired,
  history: PropTypes.object.isRequired,
  setErrorMessage: PropTypes.func.isRequired,
  userLogIn: PropTypes.func.isRequired,
  isProfileImageUploaded: PropTypes.bool
};

const mapStateToProps = (state) => _pick(state, ['app', 'auth']);

const mapDispatchToProps = {
  userLogIn: authOperations.logIn,
  userLogInExternal: authOperations.logInExternal,
  clearErrorMessage: authOperations.clearMessage,
  setErrorMessage: authOperations.setErrorMessage
};

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(SignInContainer));
